import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Pencil, Plus, Trash2, Settings2, FileText } from "lucide-react";
import { CertificazioneImport } from "@/components/CertificazioneImport";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const oggi = () => new Date().toISOString().slice(0, 10);
const thisYear = String(new Date().getFullYear());

const Kpi = ({ label, value, cls, testid, sub }) => (
  <Card className="card-elevated"><CardContent className="p-5">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-2xl font-extrabold tabular-nums ${cls || ""}`} data-testid={testid}>{fmt(value)}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </CardContent></Card>
);

const UtilizzoDialog = ({ value, onClose, onSaved, disponibile }) => {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(value); }, [value]);
  const save = async () => {
    if (num(f?.importo) <= 0) return toast.error("Inserisci l'importo");
    setBusy(true);
    const body = { importo: num(f.importo), data: f.data, note: f.note };
    try {
      const { data } = f.id ? await api.put(`/crediti/utilizzi/${f.id}`, body) : await api.post("/crediti/utilizzi", body);
      if (data.avviso_stesso_anno) toast.warning("Attenzione: stai usando crediti maturati nello stesso anno; di norma sono utilizzabili dall'anno successivo.");
      toast.success("Utilizzo registrato"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="utilizzo-dialog">
        <DialogHeader><DialogTitle>{f?.id ? "Modifica utilizzo" : "Registra utilizzo"}</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">Disponibile: <strong>{fmt(disponibile + (f.id ? num(value?.importo) : 0))}</strong></div>
            <div><Label>Importo €</Label><Input data-testid="utilizzo-importo" type="number" inputMode="decimal" step="0.01" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} className="h-11 text-lg font-bold" /></div>
            <div><Label>Data</Label><Input data-testid="utilizzo-data" type="date" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} /></div>
            <div><Label>Nota</Label><Input data-testid="utilizzo-note" placeholder="es. compensazione F24" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
            <Button data-testid="utilizzo-salva" onClick={save} disabled={busy} className="w-full h-11">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Tabella = ({ head, rows, empty, testid }) => (
  <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-secondary"><tr className="text-left">{head.map((h) => <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">{h}</th>)}</tr></thead>
      <tbody>{rows.length ? rows : <tr><td colSpan={head.length} className="px-4 py-8 text-center text-muted-foreground">{empty}</td></tr>}</tbody>
    </table>
  </CardContent></Card>
);

export const CreditiRitenute = ({ onOpenFattura }) => {
  const [anno, setAnno] = useState(thisYear);
  const [d, setD] = useState(null);
  const [uso, setUso] = useState(null);
  const load = () => api.get("/crediti", { params: { anno } }).then(({ data }) => setD(data)).catch(() => toast.error("Errore caricamento crediti"));
  useEffect(() => { load(); }, [anno]); // eslint-disable-line react-hooks/exhaustive-deps

  const anni = Array.from(new Set([thisYear, ...Object.keys(d?.per_anno || {})])).sort().reverse();
  const del = async (u) => {
    if (!window.confirm(`Eliminare l'utilizzo di ${fmt(u.importo)}?`)) return;
    try { await api.delete(`/crediti/utilizzi/${u.id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };
  const setPct = async () => {
    const v = window.prompt("Percentuale ritenuta sui bonifici parlanti (%)", String(d?.ritenuta_pct ?? 11));
    if (v === null) return;
    try { await api.put("/crediti/percentuale", { percentuale: num(v) }); toast.success("Percentuale aggiornata (vale per i nuovi pagamenti)"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const apriPdf = async (e, cid) => {
    e.stopPropagation();
    const w = window.open("", "_blank");
    try {
      const { data } = await api.get(`/crediti/certificazioni/${cid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (w) w.location.href = url; else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { w?.close(); toast.error("Impossibile aprire la certificazione"); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin mx-auto" />;
  const r = d.anno_riepilogo || {};

  return (
    <div className="space-y-4" data-testid="crediti-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Label>Anno</Label>
          <select data-testid="crediti-anno" value={anno} onChange={(e) => setAnno(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm">
            {anni.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <CertificazioneImport onDone={load} />
          <Button variant="outline" className="h-10" onClick={setPct} data-testid="crediti-pct"><Settings2 className="h-4 w-4 mr-2" />Ritenuta {d.ritenuta_pct}%</Button>
          <Button className="h-10" disabled={d.disponibile <= 0} onClick={() => setUso({ id: null, importo: "", data: oggi(), note: "" })} data-testid="crediti-registra-utilizzo"><Plus className="h-4 w-4 mr-2" />Registra utilizzo</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Kpi label={`Maturati ${anno}`} value={r.maturati} testid="crediti-maturati" sub={`utilizzabili dal ${Number(anno) + 1}`} />
        <Kpi label={`Usati (crediti ${anno})`} value={r.usati} cls="text-amber-600" testid="crediti-usati" />
        <Kpi label="Disponibile totale" value={d.disponibile} cls={d.disponibile < 0 ? "text-destructive" : "text-primary"} testid="crediti-disponibile" sub={d.disponibile < 0 ? "Attenzione: usato più credito di quello maturato (pagamento o fattura eliminati?)" : `di cui del ${anno}: ${fmt(r.disponibile)}`} />
      </div>
      <div className="text-sm font-semibold">Ritenute {anno}</div>
      <Tabella testid="crediti-ritenute" head={["Data", "Fattura", "Cliente", "Bonifico", "Ritenuta"]} empty="Nessuna ritenuta in questo anno."
        rows={d.ritenute.map((x) => (
          <tr key={x.pagamento_id} className="border-t border-border hover:bg-secondary/50 cursor-pointer" onClick={() => onOpenFattura?.(x.fattura_id)} data-testid={`ritenuta-row-${x.pagamento_id}`}>
            <td className="px-4 py-2">{x.data}</td><td className="px-4 py-2 font-semibold text-primary underline">n. {x.numero || "—"}</td>
            <td className="px-4 py-2">{x.cliente}</td><td className="px-4 py-2 tabular-nums">{fmt(x.importo)}</td><td className="px-4 py-2 tabular-nums font-bold">{fmt(x.ritenuta)}{x.certificazione_id && <Button variant="ghost" size="icon" className="h-6 w-6 ml-1" title="Apri certificazione" onClick={(e) => apriPdf(e, x.certificazione_id)} data-testid={`ritenuta-pdf-${x.pagamento_id}`}><FileText className="h-3 w-3 text-primary" /></Button>}</td>
          </tr>))} />
      <div className="text-sm font-semibold">Utilizzi registrati</div>
      <Tabella testid="crediti-utilizzi" head={["Data", "Importo", "Nota", ""]} empty="Nessun utilizzo registrato."
        rows={d.utilizzi.map((u) => (
          <tr key={u.id} className="border-t border-border" data-testid={`utilizzo-row-${u.id}`}>
            <td className="px-4 py-2">{u.data}</td><td className="px-4 py-2 tabular-nums font-bold">{fmt(u.importo)}</td><td className="px-4 py-2">{u.note}</td>
            <td className="px-4 py-2 text-right">
              <Button variant="ghost" size="icon" onClick={() => setUso({ id: u.id, importo: String(u.importo), data: u.data, note: u.note || "" })} data-testid={`utilizzo-edit-${u.id}`}><Pencil className="h-3 w-3" /></Button>
              <Button variant="ghost" size="icon" onClick={() => del(u)} data-testid={`utilizzo-del-${u.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
            </td>
          </tr>))} />
      <UtilizzoDialog value={uso} onClose={() => setUso(null)} onSaved={load} disponibile={d.disponibile} />
    </div>
  );
};
